/**
 * screens/modals/CashCountModal
 * ──────────────────────────────────────────────────────────────────────────
 * Record what was actually counted.
 *
 * THE EXPECTED FIGURE IS NOT ASKED FOR. The API takes it from the balance
 * projection or the assignment's limit, because an operator who could choose
 * both sides of a reconciliation is not reconciling anything. This form only
 * carries what a person can observe: what they counted, and for a collection,
 * what was collected and what was banked.
 *
 * The two kinds ask for different things, because they measure different things:
 *   limit       one figure — what is in the box
 *   collection  owed (from Billing), collected, deposited
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useState } from 'react';
import { ChipRow, Input, Modal } from '../../components';
import { parseMajorToMinor } from '../../lib/money';
import { recordCount } from '../../services/reconciliation/reconciliation.service';
import { useShell } from '../../context/ShellContext';
import { useModalSubmit } from './useModalSubmit';

export interface CashCountModalProps {
  assignmentId: string;
  currency: string;
  /** A collection assignment defaults to the collection count. */
  defaultKind?: 'limit' | 'collection';
}

/** The period label defaults to the current month, which is what counts are keyed by. */
function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function CashCountModal({
  assignmentId,
  currency,
  defaultKind = 'limit',
}: CashCountModalProps) {
  const { closeModal } = useShell();

  const [kind, setKind] = useState<'limit' | 'collection'>(defaultKind);
  const [period, setPeriod] = useState(currentPeriod());
  const [countedText, setCountedText] = useState('');
  const [owedText, setOwedText] = useState('');
  const [collectedText, setCollectedText] = useState('');
  const [depositedText, setDepositedText] = useState('');
  const [owedRef, setOwedRef] = useState('');

  const isCollection = kind === 'collection';
  const minor = (text: string) => (text.trim() ? parseMajorToMinor(text, currency) : null);

  const { submitting, run } = useModalSubmit({
    validate: () => {
      if (!period.trim()) return 'Which period is this count for?';
      if (!isCollection) {
        if (minor(countedText) === null) return 'Enter what you counted.';
      } else {
        if (minor(collectedText) === null) return 'Enter what was collected.';
        if (minor(depositedText) === null) return 'Enter what was deposited.';
      }
      return null;
    },
    submit: () =>
      recordCount({
        assignment_id: assignmentId,
        period: period.trim(),
        kind,
        ...(isCollection
          ? {
              ...(minor(owedText) ? { owed_minor: minor(owedText)! } : {}),
              collected_minor: minor(collectedText)!,
              deposited_minor: minor(depositedText)!,
              ...(owedRef.trim() ? { owed_ref: owedRef.trim() } : {}),
            }
          : { counted_minor: minor(countedText)! }),
      }),
    title: 'Count recorded',
    fallback: 'Any difference is now waiting to be routed or waived.',
  });

  return (
    <Modal
      visible
      title="Cash count"
      hint="What you counted. The expected figure comes from the ledger, not from this form."
      onClose={closeModal}
      primaryLabel="Record the count"
      onPrimary={run}
      primaryLoading={submitting}
    >
      <ChipRow
        label="Kind"
        required
        options={[
          { value: 'limit', label: 'Limit' },
          { value: 'collection', label: 'Collection' },
        ]}
        value={kind}
        onChange={setKind}
      />

      <Input
        label="Period"
        required
        hint="How this count is filed, e.g. 2026-08."
        value={period}
        onChangeText={setPeriod}
        monospace
      />

      {isCollection ? (
        <>
          <Input
            label="Owed"
            hint="What Billing says is owed. Leave blank to use Billing's own figure."
            value={owedText}
            onChangeText={setOwedText}
            placeholder="0.00"
            prefix={currency}
            numeric
          />
          <Input
            label="Collected"
            required
            value={collectedText}
            onChangeText={setCollectedText}
            placeholder="0.00"
            prefix={currency}
            numeric
          />
          <Input
            label="Deposited"
            required
            value={depositedText}
            onChangeText={setDepositedText}
            placeholder="0.00"
            prefix={currency}
            numeric
          />
          <Input
            label="Owed reference"
            value={owedRef}
            onChangeText={setOwedRef}
            placeholder="bil_jr_01205"
            monospace
          />
        </>
      ) : (
        <Input
          label="Counted"
          required
          hint="What is physically there."
          value={countedText}
          onChangeText={setCountedText}
          placeholder="0.00"
          prefix={currency}
          numeric
        />
      )}
    </Modal>
  );
}

export default CashCountModal;
